import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router"
import { Controller, useForm } from "react-hook-form"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useRef, useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useGeoLocation } from "@/contexts/location"
import { useCollectionCreate } from "@/hooks/useCollectionCreate"
import { saveCollectionContainers } from "@/hooks/useContainers"

import { CollectionContainersField } from "@/components/collection/CollectionContainersField"
import { SpeciesSelectInput } from "@/components/collection/SpeciesSelectInput"
import { Input } from "@nasti/ui/input"
import { Label } from "@nasti/ui/label"
import { Button } from "@nasti/ui/button"
import { Switch } from "@nasti/ui/switch"
import { Textarea } from "@nasti/ui/textarea"
import { Popover, PopoverContent, PopoverTrigger } from "@nasti/ui/popover"
import { PhenologyRangeInput } from "@nasti/ui/phenologyRangeInput"
import { CheckboxGroup } from "@nasti/ui/checkboxGroup"
import { InfoIcon, X } from "lucide-react"
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_OPTIONS,
  NewCollection,
} from "@nasti/common/types"
import { cn } from "@nasti/ui/utils"
import { UploadPhotoVariables, usePhotosMutate } from "@/hooks/usePhotosMutate"
import { PhotosForm } from "@/components/common/PhotosForm"
import { AudiosForm } from "@/components/common/AudiosForm"
import { UploadAudioVariables, useAudiosMutate } from "@/hooks/useAudiosMutate"
import { optionalPositiveNumber } from "@nasti/common/utils"
import { PersonMultiSelectField } from "@/components/common/PersonMultiSelectField"
import { DurationPickerField } from "@/components/common/DurationPickerField"
import { useCurrentUserPerson } from "@/hooks/useCurrentUserPerson"
import { ExtraFieldsAccordion } from "@/components/common/ExtraFieldsAccordion"
import { useUnsavedChangesPrompt } from "@/hooks/useUnsavedChangesPrompt"
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog"

const addCollectionSearchSchema = z.object({
  speciesId: z.string().optional(),
})

export const Route = createFileRoute("/_private/trips/$id/collections/new")({
  component: AddCollection,
  validateSearch: (search) => addCollectionSearchSchema.parse(search),
})

const schema = z
  .object({
    species_id: z.preprocess((val) => {
      if (val === "" || val === undefined) return null
      return val
    }, z.string().nullable()),
    species_uncertain: z.boolean(),
    field_name: z
      .string()
      .optional()
      .transform((val) => val || ""),
    specimen_collected: z.boolean(),
    description: z
      .string()
      .optional()
      .transform((val) => val || ""),
    phenology_start: z.number().min(-100).max(100).nullable(),
    phenology_peak: z.number().min(-100).max(100).nullable(),
    phenology_end: z.number().min(-100).max(100).nullable(),
    containers: z
      .array(
        z.object({
          container_id: z.string().uuid("Select a container"),
          amount: optionalPositiveNumber,
        }),
      )
      .default([]),
    duration: z.string().nullable(),
    material_type: z
      .array(z.enum(MATERIAL_TYPES))
      .min(1, "Select the material collected")
      .default(["seed"]),
    person_ids: z.array(z.string().uuid()).default([]),
  })
  .refine(
    (data) => {
      // Form is valid if either species_id OR field_name is provided
      return (
        Boolean(data.species_id) ||
        (data.field_name && data.field_name.trim().length > 0)
      )
    },
    {
      message: "Specimen name is required when no species is selected",
      path: ["field_name"],
    },
  )

type CollectionFormData = z.infer<typeof schema>

const defaultValues = {
  species_id: null,
  species_uncertain: false,
  field_name: "",
  specimen_collected: false,
  description: "",
  plants_sampled_estimate: null,
  phenology_start: null,
  phenology_peak: null,
  phenology_end: null,
  containers: [],
  duration: null,
  material_type: ["seed" as const],
  person_ids: [],
}

function AddCollection() {
  const { id: tripId } = useParams({
    from: "/_private/trips/$id/collections/new",
  })

  const { speciesId: initialSpeciesId } = useSearch({
    from: "/_private/trips/$id/collections/new",
  })
  const { user, organisation } = useAuth()
  const currentUserPerson = useCurrentUserPerson(organisation?.id)

  const { location, locationDisplay } = useGeoLocation()
  const { mutateAsync: createCollection } = useCollectionCreate({ tripId })
  const collectionIdRef = useRef<string>(crypto.randomUUID())
  const { createPhotoMutation } = usePhotosMutate({
    entityId: collectionIdRef.current,
    entityType: "collection",
    tripId,
  })
  const { createAudioMutation } = useAudiosMutate({
    entityId: collectionIdRef.current,
    entityType: "collection",
    tripId,
  })

  const {
    watch,
    setValue,
    register,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors, isDirty },
  } = useForm<CollectionFormData>({
    defaultValues: {
      ...defaultValues,
      species_id: initialSpeciesId,
    },
    resolver: zodResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
  })

  const speciesId = watch("species_id")

  const isSpecimenCollected = watch("specimen_collected")
  const [enterFieldName, setEnterFieldName] = useState(isSpecimenCollected)

  const handleSetIsSpecimenCollected = (val: boolean) => {
    setValue("specimen_collected", val)
    setEnterFieldName(val)
  }

  const [photos, setPhotos] = useState<UploadPhotoVariables[]>([])
  const [audios, setAudios] = useState<UploadAudioVariables[]>([])

  const { isPromptOpen, discardChanges, keepEditing, allowNavigation } =
    useUnsavedChangesPrompt({
      hasUnsavedChanges: isDirty || photos.length > 0 || audios.length > 0,
    })

  const navigate = useNavigate()

  const onSubmit = useCallback(
    async (data: CollectionFormData) => {
      if (!user || !organisation) throw new Error("Not logged in")

      if (!tripId) throw new Error("tripId must be supplied to CollectionForm")
      if (!location) throw new Error("No location available")
      const { latitude, longitude } = location
      const locationPoint = `POINT(${longitude} ${latitude})`
      const person_ids = currentUserPerson?.id
        ? data.person_ids.filter(
            (personId) => personId !== currentUserPerson.id,
          )
        : data.person_ids
      const { containers, ...collectionData } = data
      const newCollection: NewCollection = {
        ...collectionData,
        person_ids,
        id: collectionIdRef.current,
        created_by: user.id,
        collected_by: user.id,
        created_at: new Date().toISOString(),
        collected_on: new Date().toDateString(),
        location: locationPoint,
        organisation_id: organisation.id,
        trip_id: tripId,
      }
      await createCollection(newCollection)
      await saveCollectionContainers(collectionIdRef.current, containers)
      await Promise.all(
        photos.map((photo) =>
          createPhotoMutation.mutateAsync(photo, { onError: console.error }),
        ),
      )
      await Promise.all(
        audios.map((audio) =>
          createAudioMutation.mutateAsync(audio, { onError: console.error }),
        ),
      )

      if (createPhotoMutation.isError) {
        console.error(createPhotoMutation.error)
      } else {
        allowNavigation()
        navigate({ to: "/trips/$id", params: { id: tripId } })
      }
    },
    [
      user,
      organisation,
      tripId,
      location,
      createCollection,
      photos,
      audios,
      createPhotoMutation,
      createAudioMutation,
      currentUserPerson?.id,
      navigate,
      allowNavigation,
    ],
  )
  const [descriptionFocus, setDescriptionFocus] = useState(false)

  return (
    <div className="h-11/12 flex flex-col justify-between gap-2">
      <div>
        <div className="flex items-center p-2 text-2xl">New Collection</div>
        <div className="flex flex-col gap-4 px-1">
          <SpeciesSelectInput
            onSelectSpecies={(speciesId) =>
              setValue("species_id", speciesId, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            tripId={tripId}
            selectedSpeciesId={speciesId ?? undefined}
          />
          {enterFieldName && (
            <div>
              <Label>Specimen Name</Label>
              <div className="flex w-full items-center space-x-2">
                <Input
                  {...register("field_name")}
                  className="h-12 text-lg"
                  autoComplete="off"
                  tabIndex={1}
                  autoFocus
                />
                <Button
                  onClick={() =>
                    setValue("field_name", "", { shouldValidate: true })
                  }
                  className="h-12"
                  variant={"outline"}
                >
                  <X />
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <Switch id="species_uncertain" {...register("species_uncertain")} />

            <div className="flex items-center gap-2">
              <Label htmlFor="species_uncertain" className="text-lg">
                <span>Species Uncertain?</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoIcon className="h-6 w-6" />
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  Select this if you are not 100% certain about the
                  identification of the species. Set by default when entering a
                  specimen name rather than selecting a known species.
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="specimen_collected"
              render={({ field: { value } }) => (
                <Switch
                  id="specimen_collected"
                  checked={value}
                  onCheckedChange={handleSetIsSpecimenCollected}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="specimen_collected" className="text-lg">
                <span>Specimen collected?</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoIcon className="h-6 w-6" />
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  Select this if you collected a specimen of the plant.
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Controller
            control={control}
            name="person_ids"
            render={({ field }) => (
              <PersonMultiSelectField
                organisationId={organisation?.id}
                value={field.value}
                onChange={field.onChange}
                displayCurrentUser
                label="Collectors"
              />
            )}
          />

          <div>
            <Label htmlFor="location">
              <span>Location</span>
              {location?.accuracy && (
                <span className="px-2 font-light">
                  accuracy: {location?.accuracy.toFixed(1)}m
                </span>
              )}
            </Label>
            <Input
              disabled
              id="location"
              name="location"
              type="text"
              value={locationDisplay}
              className="h-12 text-lg"
            />
          </div>
          <div>
            <Controller
              control={control}
              name="material_type"
              render={({ field }) => (
                <CheckboxGroup
                  label="Material Collected"
                  size="lg"
                  options={MATERIAL_TYPE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.material_type && (
              <div className="mt-1 text-sm text-amber-600">
                {errors.material_type.message}
              </div>
            )}
          </div>
          <Controller
            control={control}
            name="containers"
            render={({ field }) => (
              <CollectionContainersField
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <ExtraFieldsAccordion>
            <Controller
              control={control}
              name="duration"
              render={({ field }) => (
                <DurationPickerField
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="phenology_start"
              render={({ field: startField }) => (
                <Controller
                  control={control}
                  name="phenology_peak"
                  render={({ field: peakField }) => (
                    <Controller
                      control={control}
                      name="phenology_end"
                      render={({ field: endField }) => (
                        <PhenologyRangeInput
                          value={[
                            startField.value,
                            peakField.value,
                            endField.value,
                          ]}
                          onValueChange={([start, peak, end]) => {
                            startField.onChange(start)
                            peakField.onChange(peak)
                            endField.onChange(end)
                          }}
                        />
                      )}
                    />
                  )}
                />
              )}
            />
            <PhotosForm onPhotosChange={({ add }) => setPhotos(add)} />
            <div>
              <Label htmlFor="description">
                <span>Description</span>
              </Label>
              <Textarea
                {...register("description")}
                id="description"
                name="description"
                className={cn(
                  "h-20 text-lg transition-all duration-500 ease-in-out",
                  descriptionFocus && "h-40",
                )}
                placeholder="Enter notes or description here"
                onFocus={() => setDescriptionFocus(true)}
                onBlur={() => setDescriptionFocus(false)}
              />
              <AudiosForm onAudiosChange={({ add }) => setAudios(add)} />
            </div>
          </ExtraFieldsAccordion>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-green-800 px-1 pt-2 md:flex-row md:gap-4">
        <Button
          variant={"secondary"}
          onClick={() => navigate({ to: "/trips/$id", params: { id: tripId } })}
          className="h-12 w-full text-lg"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="h-12 w-full text-lg"
          onClick={handleSubmit(onSubmit)}
        >
          {isSubmitting ? "Saving..." : "Save Collection"}
        </Button>
      </div>
      <UnsavedChangesDialog
        open={isPromptOpen}
        onDiscard={discardChanges}
        onKeepEditing={keepEditing}
      />
    </div>
  )
}
